/* ============================================================
   Lovable homepage behaviour.
   Scoped to the homepage sections. Safe to run many times: every
   init is guarded so Barba container swaps and Theme Editor
   section reloads never double-bind.
   ============================================================ */
(function () {
  'use strict';

  var BOUND = 'lvBound';
  var FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

  /* Breathing room kept between a chip and whatever bounds it. */
  var CHIP_GUTTER = 12;

  function all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* ---------- scroll lock (reference counted) ---------- */
  var lockCount = 0;
  var prevOverflow = '';

  function lockScroll() {
    if (lockCount === 0) {
      prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    lockCount++;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.style.overflow = prevOverflow;
  }

  /* ---------- modal controller ---------- */
  function openModal(modal, opener) {
    if (!modal || modal.dataset.lvOpen === 'true') return;
    modal.hidden = false;
    modal.dataset.lvOpen = 'true';
    modal.lvOpener = opener || document.activeElement;
    lockScroll();

    var target = modal.querySelector('[data-lv-autofocus]') || modal.querySelector(FOCUSABLE);
    if (target) target.focus();

    modal.lvKeydown = function (e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeModal(modal);
        return;
      }
      if (e.key !== 'Tab') return;
      var items = all(FOCUSABLE, modal).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', modal.lvKeydown, true);
  }

  function closeModal(modal) {
    if (!modal || modal.dataset.lvOpen !== 'true') return;
    modal.hidden = true;
    modal.dataset.lvOpen = 'false';
    unlockScroll();
    if (modal.lvKeydown) {
      document.removeEventListener('keydown', modal.lvKeydown, true);
      modal.lvKeydown = null;
    }
    if (modal.lvOpener && typeof modal.lvOpener.focus === 'function') modal.lvOpener.focus();
    modal.lvOpener = null;
  }

  function initModals(root) {
    all('[data-lv-modal]', root).forEach(function (modal) {
      if (modal.dataset[BOUND] === '1') return;
      modal.dataset[BOUND] = '1';
      modal.hidden = true;
      modal.dataset.lvOpen = 'false';
      modal.addEventListener('click', function (e) {
        if (e.target === modal || e.target.closest('[data-lv-modal-close]')) closeModal(modal);
      });
    });

    all('[data-lv-modal-open]', root).forEach(function (btn) {
      if (btn.dataset[BOUND] === '1') return;
      btn.dataset[BOUND] = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal(document.getElementById(btn.getAttribute('data-lv-modal-open')), btn);
      });
    });
  }

  /* ---------- copy to clipboard ----------
     navigator.clipboard is unavailable on insecure origins and in a few
     older mobile browsers, so there is an execCommand fallback behind a
     temporarily mounted textarea. The textarea is positioned rather than
     hidden — display:none or visibility:hidden makes it unselectable, and
     the copy silently no-ops. readOnly and a fixed position also stop iOS
     Safari zooming to the field and scrolling the page. */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      var ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (err) {
        ok = false;
      }
      document.body.removeChild(ta);
      if (ok) {
        resolve();
      } else {
        reject(new Error('copy failed'));
      }
    });
  }

  function initCopy(root) {
    all('[data-lv-copy]', root).forEach(function (btn) {
      if (btn.dataset[BOUND] === '1') return;
      btn.dataset[BOUND] = '1';

      var label = btn.querySelector('[data-lv-copy-label]');
      var live = btn.querySelector('[data-lv-copy-live]');
      var idle = label ? label.textContent : '';
      var done = btn.getAttribute('data-lv-copy-done') || 'Copied';

      btn.addEventListener('click', function () {
        var code = btn.getAttribute('data-lv-copy') || '';
        if (!code) return;

        copyText(code).then(function () {
          btn.classList.add('is-copied');
          if (label) label.textContent = done;
          /* Announced separately: the visible label is inside a button whose
             accessible name is being read anyway, and swapping it alone does
             not reliably trigger a live-region announcement. */
          if (live) live.textContent = done + ': ' + code;

          clearTimeout(btn.lvCopyTimer);
          btn.lvCopyTimer = setTimeout(function () {
            btn.classList.remove('is-copied');
            if (label) label.textContent = idle;
            if (live) live.textContent = '';
          }, 2200);
        }).catch(function () {
          /* Clipboard refused — select the code so the shopper can copy it
             by hand rather than leaving them with a dead button. */
          if (label) label.textContent = code;
          if (live) live.textContent = 'Copy failed. The code is ' + code;
        });
      });
    });
  }

  /* ---------- colourway swatches ----------
     Three distinct states, which is the whole point of the split below:

       hover / focus  — preview. Image, colour name and price change so the
                        shopper can flick through colours, but nothing is
                        chosen and no link moves.
       click          — commit. The preview sticks, the active ring moves,
                        and the title link plus the whole-card overlay are
                        repointed at that product.
       leaving        — revert to whatever is committed.

     Keeping the hrefs out of the preview step is what makes "click the card
     and you get the product you chose" true: a half-hovered colour can
     never be the thing you navigate to.

     mouseleave is bound on the swatch row rather than each swatch, so
     sliding across adjacent dots does not bounce through a revert between
     every pair.

     Each colour is a separate product, so a swatch is also a plain link to
     its own page and still works with JS off. */
  function applyPreview(card, sw) {
    if (!card || !sw) return;

    var img = card.querySelector('.lv-pick__media img');
    var color = card.querySelector('[data-lv-pick-color]');
    var price = card.querySelector('[data-lv-pick-price]');

    if (img) {
      var src = sw.getAttribute('data-img');
      var srcset = sw.getAttribute('data-srcset');
      /* srcset has to be rewritten, not just src. Left pointing at the
         previous colourway the browser keeps choosing a candidate from it
         and the new src is ignored — the image simply never changes. */
      if (src && img.getAttribute('src') !== src) {
        card.classList.add('is-swapping');
        img.onload = img.onerror = function () {
          card.classList.remove('is-swapping');
          img.onload = img.onerror = null;
        };
        if (srcset) {
          img.setAttribute('srcset', srcset);
        } else {
          img.removeAttribute('srcset');
        }
        img.setAttribute('src', src);
      }
    }

    if (color && sw.getAttribute('data-color')) {
      color.textContent = sw.getAttribute('data-color');
    }
    if (price && sw.getAttribute('data-price')) {
      price.textContent = sw.getAttribute('data-price');
    }
  }

  function commitSwatch(card, sw) {
    if (!card || !sw) return;

    applyPreview(card, sw);
    card.lvCommitted = sw;

    var url = sw.getAttribute('href');
    if (url) {
      all('[data-lv-pick-link]', card).forEach(function (link) {
        link.setAttribute('href', url);
      });
    }

    all('[data-lv-swatch]', card).forEach(function (other) {
      other.classList.toggle('lv-pick__swatch--active', other === sw);
    });
  }

  function initPicks(root) {
    all('[data-lv-pick]', root).forEach(function (card) {
      if (card.dataset[BOUND] === '1') return;
      card.dataset[BOUND] = '1';

      var swatches = all('[data-lv-swatch]', card);
      if (!swatches.length) return;

      var group = card.querySelector('.lv-pick__swatches');
      card.lvCommitted = card.querySelector('.lv-pick__swatch--active') || swatches[0];

      swatches.forEach(function (sw) {
        sw.addEventListener('mouseenter', function () {
          applyPreview(card, sw);
        });

        /* Keyboard parity with hover: tabbing across the row previews the
           same way pointing at it does. */
        sw.addEventListener('focus', function () {
          applyPreview(card, sw);
        });

        sw.addEventListener('click', function (e) {
          /* A modified click is the visitor asking for a new tab — leave the
             link alone and let the browser navigate. */
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          commitSwatch(card, sw);
        });
      });

      if (group) {
        group.addEventListener('mouseleave', function () {
          applyPreview(card, card.lvCommitted);
        });
        group.addEventListener('focusout', function (e) {
          if (!group.contains(e.relatedTarget)) applyPreview(card, card.lvCommitted);
        });
      }
    });
  }

  /* ---------- hero hotspots ----------
     Positions come from Liquid as inline percentages. This only
     decides which side the hover preview flips to, so a card near
     the right edge never overflows the frame. The x-based guess is
     good enough here: the card is a fixed 288px and desktop-only. */
  function initHotspots(root) {
    all('[data-lv-hotspot]', root).forEach(function (spot) {
      if (spot.dataset[BOUND] === '1') return;
      spot.dataset[BOUND] = '1';
      var x = parseFloat(spot.getAttribute('data-x') || '50');
      var preview = spot.querySelector('.lv-hotspot__preview');
      if (!preview) return;
      preview.classList.add(x > 55 ? 'lv-hotspot__preview--left' : 'lv-hotspot__preview--right');
    });
  }

  /* ---------- hotspot chip fit ----------
     The chip label sits to one side of the dot, and which side actually
     has room is a function of the rendered width — so it can be neither a
     fixed per-block setting nor a media query. Measure, then flip only
     when the preferred side doesn't work.

     The two sides fail differently, so they are bounded differently:

       right — .lv-hero clips at overflow:hidden, so a chip past the frame
               edge gets sliced clean off.
       left  — nothing clips it, but the headline column is sitting there.
               A label can be entirely inside the hero and still land on
               top of the h1, so the bound is the copy column's right
               edge, not the frame's left edge. Below the grid breakpoint
               the copy stacks above the figure and stops being in the
               way, at which point the frame edge is the right bound
               again — vertical overlap between the two is the test.

     The merchant's own "Label before dot" choice stays the preference and
     is restored the moment there is space for it. If neither side fits,
     the roomier one wins rather than leaving it to chance.

     Measurement anchors on the dot rather than the group, because the dot
     holds its position under both transforms — that is the whole point of
     the margin-left compensation in the section's style block, and it
     means one read is valid for both candidate sides.

     Reads are batched ahead of writes so a page of hotspots costs one
     layout pass rather than one per spot. */
  function fitHotspots() {
    var spots = all('[data-lv-hotspot]');
    if (!spots.length) return;

    var reads = [];

    spots.forEach(function (spot) {
      var frame = spot.closest('.lv-hero');
      var btn = spot.querySelector('.lv-hotspot__btn');
      var dot = spot.querySelector('.lv-hotspot__dot');
      if (!frame || !btn || !dot) return;

      var frameBox = frame.getBoundingClientRect();
      var dotBox = dot.getBoundingClientRect();
      if (!frameBox.width || !btn.offsetWidth) return;

      var copy = frame.querySelector('.lv-hero__copy');
      var figure = frame.querySelector('.lv-hero__figure');
      var leftLimit = frameBox.left;

      if (copy && figure) {
        var copyBox = copy.getBoundingClientRect();
        var figureBox = figure.getBoundingClientRect();
        if (copyBox.bottom > figureBox.top + 1) leftLimit = copyBox.right;
      }

      reads.push({
        spot: spot,
        need: btn.offsetWidth + CHIP_GUTTER,
        roomRight: frameBox.right - dotBox.left,
        roomLeft: dotBox.right - leftLimit
      });
    });

    reads.forEach(function (r) {
      var wantsLeft = r.spot.classList.contains('lv-hotspot--flipped');
      var fitsLeft = r.need <= r.roomLeft;
      var fitsRight = r.need <= r.roomRight;

      if (wantsLeft && !fitsLeft) {
        wantsLeft = fitsRight ? false : r.roomLeft >= r.roomRight;
      } else if (!wantsLeft && !fitsRight) {
        wantsLeft = fitsLeft ? true : r.roomLeft > r.roomRight;
      }

      r.spot.classList.toggle('lv-hotspot--fit-left', wantsLeft);
      r.spot.classList.toggle('lv-hotspot--fit-right', !wantsLeft);
    });
  }

  /* Re-measure on anything that can change the frame or the chip width:
     viewport resize, the hero image finally laying out, and the webfont
     swapping in under font-display:swap. Bound once, globally. */
  var fitBound = false;

  function bindFit() {
    if (fitBound) return;
    fitBound = true;

    var queued = null;
    var schedule = function () {
      if (queued) return;
      queued = window.requestAnimationFrame(function () {
        queued = null;
        fitHotspots();
      });
    };

    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('load', schedule);

    all('.lv-hero__img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', schedule);
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(schedule).catch(function () {});
    }
  }

  /* ---------- press rotator ---------- */
  function initPress(root) {
    all('[data-lv-press]', root).forEach(function (wrap) {
      if (wrap.dataset[BOUND] === '1') return;
      wrap.dataset[BOUND] = '1';

      var tabs = all('[data-lv-press-tab]', wrap);
      var quotes = all('[data-lv-press-quote]', wrap);
      if (tabs.length < 2) return;

      var index = 0;
      var timer = null;
      var delay = parseInt(wrap.getAttribute('data-interval') || '5000', 10);
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function show(i) {
        index = (i + tabs.length) % tabs.length;
        tabs.forEach(function (t, n) {
          var on = n === index;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        quotes.forEach(function (q, n) {
          q.setAttribute('data-active', n === index ? 'true' : 'false');
        });
      }

      function start() {
        if (reduce || timer) return;
        timer = setInterval(function () {
          show(index + 1);
        }, delay);
      }

      function stop() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          stop();
          show(i);
        });
        tab.addEventListener('focus', stop);
        tab.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            stop();
            show(index + 1);
            tabs[index].focus();
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            stop();
            show(index - 1);
            tabs[index].focus();
          }
        });
      });

      wrap.addEventListener('mouseenter', stop);
      wrap.addEventListener('mouseleave', start);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) {
              start();
            } else {
              stop();
            }
          });
        }, { threshold: 0.1 }).observe(wrap);
      } else {
        start();
      }

      wrap.lvStop = stop;
      show(0);
    });
  }

  /* ---------- lazy autoplay video ----------
     Decorative loops only start once visible, and never on
     reduced-motion or save-data connections. */
  function initVideos(root) {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var saveData = navigator.connection && navigator.connection.saveData;

    all('[data-lv-video]', root).forEach(function (video) {
      if (video.dataset[BOUND] === '1') return;
      video.dataset[BOUND] = '1';

      if (reduce || saveData) {
        video.removeAttribute('autoplay');
        return;
      }

      if (!('IntersectionObserver' in window)) {
        video.play().catch(function () {});
        return;
      }

      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var v = en.target;
          if (en.isIntersecting) {
            if (v.dataset.src && !v.src) v.src = v.dataset.src;
            v.play().catch(function () {});
          } else {
            v.pause();
          }
        });
      }, { rootMargin: '200px' }).observe(video);
    });
  }

  /* ---------- boot ---------- */
  function init(root) {
    var scope = root || document;
    initModals(scope);
    initCopy(scope);
    initPicks(scope);
    initHotspots(scope);
    initPress(scope);
    initVideos(scope);
    bindFit();
    fitHotspots();
  }

  function boot() {
    init(document);
  }

  function teardown(root) {
    all('[data-lv-press]', root).forEach(function (w) {
      if (w.lvStop) w.lvStop();
    });
    all('[data-lv-modal]', root).forEach(closeModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Platter navigates through Barba, which swaps the <main>
     container without a page load. Re-init after each transition
     and tear down timers before leaving. */
  if (window.barba && window.barba.hooks) {
    window.barba.hooks.beforeLeave(function () {
      teardown(document);
    });
    window.barba.hooks.afterEnter(function () {
      boot();
    });
  } else {
    document.addEventListener('barba:after', boot);
  }

  /* Theme Editor: re-init only the section the merchant changed. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    teardown(e.target);
  });

  /* Dragging a hotspot in the editor moves it without a section reload,
     so the fit has to be recomputed as the inline position changes. */
  document.addEventListener('lv:hotspot:moved', fitHotspots);
})();
